import { useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faCopy,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import {
  faFacebook,
  faLinkedin,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Modal, useModal } from '@appsumo/dorado-react';

import {
  FACEBOOK_SHARE_URL,
  LINKEDIN_SHARE_URL,
  TWITTER_SHARE_URL,
} from '~/lib/util/constants';
import { openExternalWindow } from '~/lib/util';
import useCopyToClipboard from '~/lib/util/hooks/copyToClipboard';

type SocialIconType = {
  name: string;
  icon: IconProp;
  color?: string;
};

export type UTMParamsConfig = {
  email: string;
  facebook: string;
  linkedin: string;
  twitter: string;
  text: string;
};

type SocialLinkTypes = {
  [key: string]: string;
  email: string;
  facebook: string;
  linkedin: string;
  twitter: string;
};

const socialButtonConfig: Array<SocialIconType> = [
  {
    name: 'email',
    icon: faEnvelope as IconProp,
    color: '#2F4B4C',
  },
  {
    name: 'facebook',
    icon: faFacebook as IconProp,
    color: '#4267B2',
  },
  {
    name: 'linkedin',
    icon: faLinkedin as IconProp,
    color: '#0072B1',
  },
  {
    name: 'twitter',
    icon: faXTwitter as IconProp,
    color: '#000000',
  },
];

// Helper Functions

function getTwitterText(title: string, subheader?: string) {
  let value = `Don’t miss out on ${title}`;
  if (subheader) {
    value += ` - ${subheader}`;
  }
  return encodeURIComponent(
    `${value}.\u000aThis deal is only on AppSumo for a limited time!`,
  );
}

function getSocialNetworkURL({
  email,
  facebook,
  twitter,
  linkedin,
  shareableText,
  title,
  subheader,
}: {
  email: string;
  facebook: string;
  twitter: string;
  linkedin: string;
  shareableText: string;
  title: string;
  subheader?: string;
}) {
  // Additional social network parameters (i.e: utm parameters) to append
  // to the shareableText value.
  const params = {
    email: [
      `mailto:?body=${encodeURIComponent(shareableText + email)}`,
      `subject=${encodeURIComponent(`Share ${title}`)}`,
    ],
    facebook: [
      `u=${encodeURIComponent(shareableText)}`,
      `quote=${encodeURIComponent(shareableText + facebook)}`,
    ],
    twitter: [
      `url=${encodeURIComponent(shareableText + twitter)}`,
      `text=${getTwitterText(title, subheader)}`,
    ],
    linkedin: [`url=${encodeURIComponent(shareableText + linkedin)}`],
  };
  return {
    email: `${params.email.join('&')}`,
    facebook: `${FACEBOOK_SHARE_URL}${params.facebook.join('&')}`,
    twitter: `${TWITTER_SHARE_URL}${params.twitter.join('&')} `,
    linkedin: `${LINKEDIN_SHARE_URL}${params.linkedin.join('&')} `,
  };
}

// Component Functions

function SocialButton({
  index,
  icon,
  openSocialWindow,
}: {
  index: number;
  icon: SocialIconType;
  openSocialWindow: (socialName: string) => void;
}) {
  return (
    <button
      key={icon.name}
      className={`${
        index === 0 ? '' : 'ml-3'
      } rounded-full bg-gray-200 p-3 leading-3`}
      onClick={() => openSocialWindow(icon.name)}
    >
      <FontAwesomeIcon
        className="h-7 w-7"
        icon={icon.icon}
        width="28"
        height="28"
        color={icon.color}
      />
    </button>
  );
}

function ModalContent({
  socialButtonConfig,
  shareableLinkWithParams,
  labelText,
  copyToClipboard,
  socialShare,
}: {
  socialButtonConfig: Array<SocialIconType>;
  shareableLinkWithParams: string;
  labelText: string;
  copyToClipboard: () => void;
  socialShare: (socialName: string) => void;
}) {
  return (
    <div className="w-full">
      {socialButtonConfig.map((icon, index) => (
        <SocialButton
          key={icon.name}
          index={index}
          icon={icon}
          openSocialWindow={() => socialShare(icon.name)}
        />
      ))}
      <div className="relative mt-4 flex w-full grow flex-col">
        <label
          htmlFor="shareable-link"
          className="mb-2 font-header font-semibold text-midnight"
        >
          {labelText}
        </label>
        <input
          type="text"
          name="shareable-link"
          className="rounded-[4px] border border-gravel bg-iceberg py-2 px-4 pr-8 text-midnight outline-none"
          readOnly
          value={shareableLinkWithParams}
        />
        <button
          className="absolute bottom-0 right-0 m-1 w-fit"
          onClick={() => copyToClipboard()}
        >
          <FontAwesomeIcon
            icon={faCopy}
            width="24"
            height="24"
            className="m-1"
          />
        </button>
      </div>
    </div>
  );
}

export default function ShareModal({
  buttonText = 'Share',
  showButtonText = true,
  modalTitle,
  subheader,
  labelText = 'Or copy link',
  href,
  className = '',
  params,
}: {
  buttonText?: string;
  showButtonText?: boolean;
  modalTitle: string;
  subheader?: string;
  labelText?: string;
  href?: string;
  className?: string;
  params: UTMParamsConfig;
}) {
  const [, copyToClipboard] = useCopyToClipboard();

  const shareableLinkWithParams = `${href}${params.text || ''}`;
  const socialLinks: SocialLinkTypes = useMemo(
    () =>
      getSocialNetworkURL({
        email: params.email,
        facebook: params.facebook,
        twitter: params.twitter,
        linkedin: params.linkedin,
        shareableText: shareableLinkWithParams,
        title: modalTitle,
        subheader,
      }),
    [params, shareableLinkWithParams, modalTitle, subheader],
  );

  const openSocialWindow = (socialName: string) => {
    const windowName =
      socialName !== 'email'
        ? `share-modal-${Math.round(Math.random() * 10000)}`
        : '_self';
    openExternalWindow({
      url: socialLinks[socialName],
      windowName,
      popup: socialName !== 'email',
    });
  };

  const cipboardCopy = async () => {
    const copied = await copyToClipboard(shareableLinkWithParams);
    if (copied) {
      toast.success('Link copied to clipboard!', {
        id: 'clipboard',
      });
    } else {
      toast.error('Failed to copy link to clipboard.', {
        id: 'clipboard',
      });
    }
  };

  const [showModal, hideModal] = useModal(
    () => (
      <Modal
        hideModal={hideModal}
        title={modalTitle}
        className="absolute top-32 w-[500px] max-w-[90%] md:pb-8"
      >
        <ModalContent
          socialButtonConfig={socialButtonConfig}
          shareableLinkWithParams={shareableLinkWithParams}
          labelText={labelText}
          copyToClipboard={cipboardCopy}
          socialShare={openSocialWindow}
        />
      </Modal>
    ),
    [modalTitle, shareableLinkWithParams, labelText],
  );

  return (
    <div className={`flex items-center justify-end ${className}`}>
      <button className="flex items-center" onClick={() => showModal()}>
        <FontAwesomeIcon
          icon={faArrowUpFromBracket}
          width="16"
          height="16"
          className={showButtonText ? 'mr-2' : ''}
        />
        {showButtonText && buttonText}
      </button>
    </div>
  );
}
